import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRightLeft,
  faRotateRight,
  faFileLines,
  faCalendarDays,
  faCartPlus,
  faMinus,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./ApuestasPage.module.css";
import { useCart } from "../../../context/cartContext";
import { lotteryService, formatCurrency } from "../../../services/api";
import { SeriesWebSocketClient } from "../../../services/seriesWebSocket";
import noImagePlaceholder from "../../../assets/img/not-image.png";
import { createLocalId } from "../../../utils/id";
import { Spinner, ErrorBanner, FeedbackMessage } from "../../../components/ui";
import useNowTick from "../../../hooks/useNowTick";
import { Reveal } from "../../../components/motion";
import BetSummarySidebar from "./components/BetSummarySidebar";
import LotteryPicker from "./components/LotteryPicker";
import LotteryLogoStrip from "./components/LotteryLogoStrip";
import {
  isLotteryExtra,
  isLotteryComingSoon,
  openPrizePlanInNewView,
  getLotteryDrawDateTime,
  isLotteryBettingClosed,
  getClosingScheduleSummary,
  getEffectiveClosingTime,
  getDailyBettingClosesAt,
  getOpenClosingBadgeText,
  getPurchaseDeadlineLabel,
} from "./lotteryHelpers";

const DESKTOP_QUERY = "(min-width: 900px)";
const SLOT_MACHINE_ICON = `${process.env.PUBLIC_URL || ""}/images/slot-machine.svg`;

/* Tragaperras de "Dame suerte": los 4 rodillos giran juntos, los 3 primeros paran
   escalonados y el 4º desacelera con "fricción" hasta caer en el número del backend. */
const SLOT_TICK_MS = 60; // cambio de dígito mientras gira a toda velocidad
const SLOT_STOP_TIMES = [500, 800, 1100]; // parada de los rodillos 1-3 (desde la respuesta)
const SLOT_LAST_START = 1150; // inicio de la desaceleración del 4º rodillo
const SLOT_DECEL_STEPS = [80, 120, 180, 260, 360, 480]; // ms entre dígitos del 4º rodillo
const SLOT_DECEL_CSS = [0.1, 0.15, 0.22, 0.32, 0.45, 0.6]; // duración CSS del giro (s)
const SLOT_SETTLE_MS = 150; // pausa antes de devolver los rodillos a modo edición
const SPIN_SPEED_DEFAULT = 0.08;
const REELS_IDLE = [false, false, false, false];

const prefersReducedMotion = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};

export default function ApuestaView() {
  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart, getFraccionesEnCarrito } = useCart();
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];
  const seriesClientRef = useRef(null);
  const pickerRef = useRef(null);
  const pickerSearchRef = useRef(null);
  const mobileSelectRef = useRef(null);
  const selectedIdRef = useRef(null);
  const mountedRef = useRef(true);
  const spinTimersRef = useRef({ interval: null, timeouts: [] });
  const spinIdRef = useRef(0);

  // Estados
  const [lotteries, setLotteries] = useState([]);
  // "Próximamente": se muestran en gris pero nunca se pueden seleccionar ni comprar.
  const [comingSoonLotteries, setComingSoonLotteries] = useState([]);
  const [selectedLottery, setSelectedLottery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [numero, setNumero] = useState(["", "", "", ""]);
  const [series, setSeries] = useState(""); // Serie seleccionada
  const [availableSeries, setAvailableSeries] = useState([]);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [shouldFetchSeries, setShouldFetchSeries] = useState(false); // Solo buscar cuando se confirme
  const [cantidad, setCantidad] = useState(1); // Cantidad de fracciones
  const [isAnimating, setIsAnimating] = useState(false); // "Dame suerte" en curso
  const [reelsActive, setReelsActive] = useState(false); // Rodillos mostrando la animación
  const [slotNumbers, setSlotNumbers] = useState([0, 0, 0, 0]); // Dígitos visibles al girar
  const [spinningSlots, setSpinningSlots] = useState(REELS_IDLE); // Cuáles están girando
  const [slowingDown, setSlowingDown] = useState(false); // El 4º está desacelerando
  const [spinSpeed, setSpinSpeed] = useState(SPIN_SPEED_DEFAULT); // Velocidad CSS del 4º
  const [imageError, setImageError] = useState(false);
  const [formError, setFormErrorMsg] = useState("");
  // Dónde mostrar el error: en el constructor de apuesta o junto al botón del carrito.
  const [errorWhere, setErrorWhere] = useState("builder");
  const [loadError, setLoadError] = useState("");
  const now = useNowTick();
  const selectedClosed = isLotteryBettingClosed(selectedLottery, now);

  const setFormError = useCallback((message, where = "builder") => {
    setFormErrorMsg(message);
    if (message) setErrorWhere(where);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    selectedIdRef.current = selectedLottery?.id ?? null;
  }, [selectedLottery]);

  // Detiene intervalos/timeouts de la tragaperras (cambio de lotería, error, desmontaje).
  const stopSpinTimers = useCallback(() => {
    const timers = spinTimersRef.current;
    if (timers.interval) clearInterval(timers.interval);
    timers.timeouts.forEach(clearTimeout);
    spinTimersRef.current = { interval: null, timeouts: [] };
  }, []);

  const resetReels = useCallback(() => {
    stopSpinTimers();
    setReelsActive(false);
    setSpinningSlots(REELS_IDLE);
    setSlowingDown(false);
    setSpinSpeed(SPIN_SPEED_DEFAULT);
  }, [stopSpinTimers]);

  useEffect(() => () => stopSpinTimers(), [stopSpinTimers]);

  // Cargar loterías al inicio
  useEffect(() => {
    const fetchLotteries = async () => {
      try {
        const [result, soonResult] = await Promise.all([
          lotteryService.getLotteryInfo(),
          lotteryService.getComingSoonLotteries(),
        ]);
        const soonList = soonResult?.ok && Array.isArray(soonResult.data)
          ? soonResult.data
          : [];
        const soonIds = new Set(soonList.map((l) => String(l.id)));
        setComingSoonLotteries(soonList);
        if (result.ok) {
          // Defensivo: una lotería marcada "Próximamente" nunca entra a la lista comprable.
          const normalized = (Array.isArray(result.data) ? result.data : []).filter(
            (l) => !isLotteryComingSoon(l) && !soonIds.has(String(l?.id))
          );
          // Extras primero (como en la app: visibles y destacados)
          const sorted = [...normalized].sort((a, b) => {
            const aExtra = isLotteryExtra(a) ? 0 : 1;
            const bExtra = isLotteryExtra(b) ? 0 : 1;
            return aExtra - bExtra;
          });
          setLotteries(sorted);
          setLoadError("");

          if (location.state?.selectedLottery) {
            const lottery = sorted.find(
              (l) => l.id === location.state.selectedLottery.id
            );
            if (lottery && !isLotteryBettingClosed(lottery)) {
              setSelectedLottery(lottery);
            }
          }
        } else {
          setLoadError(
            "No pudimos cargar las loterías. Revisa tu conexión e inténtalo de nuevo."
          );
        }
      } catch (err) {
        console.error("Error loading lotteries:", err);
        setLoadError("Error de conexión al cargar loterías.");
      } finally {
        setLoading(false);
      }
    };

    fetchLotteries();
  }, [location.state]);

  // Obtener máximo de fracciones de la lotería seleccionada
  const maxFracciones = selectedLottery
    ? parseInt(selectedLottery.number_fractions, 10) || 1
    : 1;

  // Precio por fracción
  const precioFraccion = selectedLottery ? parseFloat(selectedLottery.fraction_value) : 0;

  // Total a pagar = precio por fracción × cantidad de fracciones
  const total = precioFraccion * cantidad;

  // Como Flutter SelectLottery: al elegir lotería, fracciones = máximo permitido
  useEffect(() => {
    if (selectedLottery) {
      setCantidad(parseInt(selectedLottery.number_fractions, 10) || 1);
    }
    setImageError(false);
  }, [selectedLottery]);

  // WebSocket de series (mismo flujo que Flutter: SeriesWebSocketService)
  useEffect(() => {
    const client = new SeriesWebSocketClient();
    seriesClientRef.current = client;
    client.connect();
    const unsub = client.subscribe((series) => {
      setAvailableSeries(series);
      setLoadingSeries(false);
      // La serie elegida solo vale para el número consultado: si el número
      // cambió y la nueva lista no la trae, se descarta (evita vender una
      // combinación que no existe en la emisión).
      setSeries((prev) => (prev && series.includes(prev) ? prev : ""));
    });
    return () => {
      unsub();
      client.disconnect();
      seriesClientRef.current = null;
    };
  }, []);

  const fetchSeries = useCallback((lotteryId, fullNumber) => {
    if (!lotteryId || fullNumber.length !== 4) return;
    setLoadingSeries(true);
    seriesClientRef.current?.querySeries(lotteryId, fullNumber);
  }, []);

  // Solo consultar series cuando shouldFetchSeries es true
  useEffect(() => {
    if (shouldFetchSeries && selectedLottery) {
      const fullNumber = numero.join("");
      if (fullNumber.length === 4) {
        fetchSeries(selectedLottery.id, fullNumber);
      }
      setShouldFetchSeries(false);
    }
  }, [shouldFetchSeries, numero, selectedLottery, fetchSeries]);

  // Cambiar dígitos manualmente - solo buscar series cuando se complete el 4to dígito
  const handleNumeroChange = (index, rawValue) => {
    // Si el rodillo ya tenía dígito y el cursor no lo seleccionaba, queda el último tecleado.
    const value = rawValue.replace(/[^0-9]/g, "").slice(-1);
    const copia = [...numero];
    copia[index] = value;
    setNumero(copia);
    // Otro número = otras series: limpiar la serie elegida y la lista vieja.
    setSeries("");
    setAvailableSeries([]);

    // Auto-focus al siguiente
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    // Si es el último dígito (índice 3) y tiene valor, buscar series
    if (index === 3 && value) {
      // Verificar que todos los demás también tengan valor
      const allFilled = copia.every(n => n !== "");
      if (allFilled) {
        setShouldFetchSeries(true);
      }
    }
  };

  // Manejar tecla backspace
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !numero[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
      return;
    }
    // Reescribir el mismo dígito no dispara onChange: igual se avanza al siguiente.
    if (/^\d$/.test(e.key) && numero[index] === e.key && index < 3) {
      e.preventDefault();
      inputRefs[index + 1].current?.focus();
    }
  };

  // Botón circular "refrescar": limpia el número (y con él la serie y la lista de series).
  const clearNumber = () => {
    if (isAnimating) return;
    setNumero(["", "", "", ""]);
    setSeries("");
    setAvailableSeries([]);
    setLoadingSeries(false);
    setFormError("");
    inputRefs[0].current?.focus();
  };

  // "Dame suerte" estilo tragaperras. El número y la serie los elige el backend ENTRE
  // LAS COMBINACIONES CARGADAS de la lotería (random_combination); los rodillos giran
  // mientras llega la respuesta y luego paran sobre ese resultado. Si el backend no
  // responde (o la lotería no tiene emisión cargada) se avisa y no se inventa un
  // número que podría no existir.
  const generarAleatorios = async () => {
    if (!selectedLottery) {
      setFormError("Primero selecciona una lotería");
      return;
    }
    if (isLotteryBettingClosed(selectedLottery)) {
      setFormError(getClosingScheduleSummary(selectedLottery));
      return;
    }
    if (isAnimating) return;
    setFormError("");

    const spinId = spinIdRef.current + 1;
    spinIdRef.current = spinId;
    const lotteryId = selectedLottery.id;
    const animate = !prefersReducedMotion();
    const later = (fn, ms) => {
      spinTimersRef.current.timeouts.push(setTimeout(fn, ms));
    };

    setIsAnimating(true);
    setAvailableSeries([]);
    setSeries("");

    // Todos los rodillos empiezan a girar (desde los dígitos actuales) ya mismo,
    // mientras llega la respuesta del backend.
    const spinning = [true, true, true, true];
    const currentNums = numero.map((d) => (d === "" ? 0 : Number(d)));
    if (animate) {
      stopSpinTimers();
      setSlotNumbers([...currentNums]);
      setSpinningSlots([true, true, true, true]);
      setSlowingDown(false);
      setSpinSpeed(SPIN_SPEED_DEFAULT);
      setReelsActive(true);
      spinTimersRef.current.interval = setInterval(() => {
        for (let i = 0; i < 4; i++) {
          if (spinning[i]) currentNums[i] = (currentNums[i] + 1) % 10;
        }
        setSlotNumbers([...currentNums]);
      }, SLOT_TICK_MS);
    }

    let finalNumbers;
    let luckySeries = "";
    try {
      const res = await lotteryService.randomCombination({ lottery: selectedLottery.name });
      if (res.ok && /^\d{4}$/.test(res.data.number)) {
        finalNumbers = res.data.number.split("").map(Number);
        luckySeries = res.data.series;
      }
    } catch (err) {
      console.error("random_combination:", err);
    }

    // Desmontado, o el giro fue cancelado (cambió de lotería → resetBetForm): se descarta.
    if (!mountedRef.current || spinIdRef.current !== spinId) return;
    if (selectedIdRef.current !== lotteryId) {
      resetReels();
      setIsAnimating(false);
      return;
    }

    if (!finalNumbers) {
      resetReels();
      setIsAnimating(false);
      setFormError(
        "No pudimos elegir un número para esta lotería. Intenta de nuevo o digita el tuyo."
      );
      return;
    }

    const finish = () => {
      setNumero(finalNumbers.map(String));
      // La serie que eligió el backend queda seleccionada; la lista que
      // llega por WebSocket la conserva porque viene de la misma tabla.
      setSeries(luckySeries);
      setIsAnimating(false);
      setShouldFetchSeries(true);
    };

    // Movimiento reducido: sin giro, solo se ponen los dígitos.
    if (!animate) {
      finish();
      return;
    }

    // Los 3 primeros rodillos paran escalonados en su dígito.
    SLOT_STOP_TIMES.forEach((stopTime, index) => {
      later(() => {
        spinning[index] = false;
        currentNums[index] = finalNumbers[index];
        setSlotNumbers([...currentNums]);
        setSpinningSlots((prev) => {
          const next = [...prev];
          next[index] = false;
          return next;
        });
      }, stopTime);
    });

    // El 4º desacelera con efecto de fricción: cada paso más lento (tiempo entre
    // dígitos y velocidad CSS) y el último paso cae justo en el dígito del backend.
    later(() => {
      clearInterval(spinTimersRef.current.interval);
      spinTimersRef.current.interval = null;
      setSlowingDown(true);
      const steps = SLOT_DECEL_STEPS.length;
      currentNums[3] = (((finalNumbers[3] - steps) % 10) + 10) % 10;
      let step = 0;

      const slowDown = () => {
        if (step >= steps) {
          currentNums[3] = finalNumbers[3];
          setSlotNumbers([...currentNums]);
          setSpinningSlots(REELS_IDLE);
          setSlowingDown(false);
          setSpinSpeed(SPIN_SPEED_DEFAULT);
          later(() => {
            stopSpinTimers();
            setReelsActive(false);
            finish();
          }, SLOT_SETTLE_MS);
          return;
        }
        currentNums[3] = (currentNums[3] + 1) % 10;
        setSlotNumbers([...currentNums]);
        setSpinSpeed(SLOT_DECEL_CSS[step]);
        later(slowDown, SLOT_DECEL_STEPS[step]);
        step += 1;
      };

      slowDown();
    }, SLOT_LAST_START);
  };

  const resetBetForm = useCallback(() => {
    spinIdRef.current += 1; // invalida un "Dame suerte" en curso
    resetReels();
    setNumero(["", "", "", ""]);
    setSlotNumbers([0, 0, 0, 0]);
    setIsAnimating(false);
    setSeries("");
    setAvailableSeries([]);
    setImageError(false);
  }, [resetReels]);

  const handleSelectLottery = useCallback(
    (lottery) => {
      setFormError("");
      if (lottery && isLotteryComingSoon(lottery)) return;
      if (lottery && isLotteryBettingClosed(lottery)) {
        setFormError(getClosingScheduleSummary(lottery));
        return;
      }
      if (lottery?.id === selectedLottery?.id) return;
      setSelectedLottery(lottery || null);
      resetBetForm();
    },
    [selectedLottery?.id, resetBetForm, setFormError]
  );

  // "⇄ Cambiar": en escritorio lleva al selector de tarjetas (columna izquierda);
  // en móvil enfoca/abre el desplegable de loterías.
  const handleChangeLottery = () => {
    const behavior = prefersReducedMotion() ? "auto" : "smooth";
    let isDesktop = true;
    try {
      isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
    } catch {
      /* matchMedia no disponible */
    }
    if (isDesktop) {
      pickerRef.current?.scrollIntoView({ behavior, block: "start" });
      pickerSearchRef.current?.focus({ preventScroll: true });
      return;
    }
    // Móvil: lleva a la fila de logos y enfoca la tarjeta elegida (o la primera).
    const strip = mobileSelectRef.current;
    if (!strip) return;
    strip.scrollIntoView({ behavior, block: "start" });
    const card = strip.querySelector('button[aria-pressed="true"]') || strip.querySelector("button:not([disabled])");
    card?.focus({ preventScroll: true });
  };

  const handleBack = () => {
    if (location.key && location.key !== "default") {
      navigate(-1);
    } else {
      navigate("/welcome");
    }
  };

  const handlePrizePlan = () => {
    const opened = openPrizePlanInNewView(selectedLottery);
    if (!opened) {
      setFormError("Plan de premios no disponible para esta lotería.");
    } else {
      setFormError("");
    }
  };

  // Añadir al carrito (permitido sin auth)
  const enviarDatos = () => {
    setFormError("");
    const fail = (message) => setFormError(message, "cart");
    if (!selectedLottery) {
      fail("Seleccione una lotería");
      return;
    }

    if (isLotteryBettingClosed(selectedLottery)) {
      fail(getClosingScheduleSummary(selectedLottery));
      return;
    }

    if (numero.some((n) => n === "")) {
      fail("Complete los 4 dígitos del número");
      return;
    }

    if (!series) {
      fail("Seleccione una serie");
      return;
    }

    if (!availableSeries.includes(series)) {
      setSeries("");
      fail("La serie elegida no está disponible para este número. Selecciona una de la lista.");
      return;
    }

    // Misma forma que Flutter AddCarElements (add_card_buttom.dart)
    const betData = {
      id: createLocalId(),
      lottery: selectedLottery.name,
      loteria: selectedLottery.name,
      imagen: selectedLottery.image,
      image: selectedLottery.image,
      numero: numero.join(""),
      serie: series,
      series,
      fracciones: String(cantidad),
      cantidad,
      precio: precioFraccion,
      value: total,
      lotteryId: selectedLottery.id,
      precioFraccion,
      total,
      maxFracciones,
      sorte: selectedLottery.sorteo,
      sorteo: selectedLottery.sorteo,
      drawEndsAt: getLotteryDrawDateTime(selectedLottery),
      drawDate: getLotteryDrawDateTime(selectedLottery),
      purchaseClosingTime: getEffectiveClosingTime(selectedLottery),
      drawDayClosingTime: getDailyBettingClosesAt(selectedLottery),
      fecha: new Date().toISOString(),
    };

    // Verificar cuántas fracciones ya hay en el carrito para esta combinación
    const fraccionesEnCarrito = getFraccionesEnCarrito(selectedLottery.id, numero.join(""), series);
    const fraccionesDisponibles = maxFracciones - fraccionesEnCarrito;

    if (fraccionesDisponibles <= 0) {
      fail(
        `Ya tienes todas las fracciones (${maxFracciones}) de esta serie en el carrito`
      );
      return;
    }

    if (cantidad > fraccionesDisponibles) {
      fail(
        `Solo puedes agregar ${fraccionesDisponibles} fracción(es) más de esta serie. Ya tienes ${fraccionesEnCarrito} en el carrito.`
      );
      return;
    }

    addToCart(betData);

    // No reseteamos los datos para permitir agregar más fracciones
    // Solo actualizamos la cantidad disponible si el usuario quiere agregar más
    const nuevasFraccionesDisponibles = fraccionesDisponibles - cantidad;
    if (nuevasFraccionesDisponibles <= 0) {
      // Si ya no quedan fracciones de esta serie, resetear la serie para elegir otra
      setSeries("");
    }
  };

  if (loading) {
    return (
      <div className={styles.apuestaContainer}>
        <Spinner label="Cargando loterías…" />
      </div>
    );
  }

  const numberComplete = numero.join("").length === 4;
  const canAddToCart = Boolean(selectedLottery && series && !selectedClosed);
  const cartHint =
    selectedLottery && !selectedClosed && !series && numberComplete && !loadingSeries
      ? "Selecciona una serie para añadir al carrito."
      : selectedLottery && !selectedClosed && !numberComplete
        ? "Completa tu número de 4 dígitos o usa «Dame suerte»."
        : "";
  const prize = selectedLottery
    ? selectedLottery.prize_amount ?? selectedLottery.premio ?? selectedLottery.amount
    : null;
  const deadline = getPurchaseDeadlineLabel(selectedLottery);
  const openBadge = selectedLottery ? getOpenClosingBadgeText(selectedLottery, now) : null;
  const builderError = formError && errorWhere === "builder" ? formError : "";
  const cartError = formError && errorWhere === "cart" ? formError : "";

  return (
    <div
      className={`${styles.apuestaContainer} ${
        selectedLottery ? styles.withMobileBar : ""
      }`}
    >
      {loadError ? (
        <ErrorBanner
          message={loadError}
          onRetry={() => window.location.reload()}
        />
      ) : null}
      <div className={styles.betLayout}>
        {/* Paso 1 (izquierda en escritorio): tarjetas de loterías */}
        <Reveal as="aside" variant="sharedAxisX" className={styles.pickerColumn} ref={pickerRef}>
          <LotteryPicker
            lotteries={lotteries}
            comingSoon={comingSoonLotteries}
            selectedId={selectedLottery?.id ?? null}
            onSelect={handleSelectLottery}
            now={now}
            searchInputRef={pickerSearchRef}
          />
        </Reveal>

        {/* Paso 2 (centro): constructor de la apuesta */}
        <Reveal delay={0.08} className={styles.betMain}>
          <div className={styles.mobileLotterySelect}>
            {/* Celular: tarjetas con logo deslizables (antes un desplegable de solo texto) */}
            <LotteryLogoStrip
              lotteries={lotteries}
              comingSoon={comingSoonLotteries}
              selectedId={selectedLottery?.id ?? null}
              onSelect={handleSelectLottery}
              now={now}
              stripRef={mobileSelectRef}
            />
          </div>

          <section
            className={styles.betCard}
            aria-label={selectedLottery ? `Apuesta en ${selectedLottery.name}` : "Arma tu apuesta"}
          >
            {selectedLottery ? (
              <>
                <header className={styles.builderHeader}>
                  <div className={styles.logoBadge}>
                    <img
                      src={imageError || !selectedLottery.image ? noImagePlaceholder : selectedLottery.image}
                      alt={selectedLottery.name}
                      className={styles.logoImg}
                      onError={() => setImageError(true)}
                    />
                  </div>
                  <div className={styles.headerText}>
                    <p className={styles.builderStep}>Paso 2 · Tu apuesta</p>
                    <h2 className={styles.lotteryName}>{selectedLottery.name}</h2>
                    {deadline ? (
                      <p className={styles.deadline}>
                        <FontAwesomeIcon icon={faCalendarDays} aria-hidden />
                        <span>
                          Compra hasta <strong>{deadline}</strong>
                        </span>
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={styles.changeBtn}
                    onClick={handleChangeLottery}
                    aria-label="Cambiar de lotería"
                  >
                    <FontAwesomeIcon icon={faRightLeft} aria-hidden />
                    Cambiar
                  </button>
                </header>

                <div className={styles.metaChips}>
                  {selectedLottery.sorteo != null ? (
                    <span className={styles.chip}>Sorteo #{selectedLottery.sorteo}</span>
                  ) : null}
                  {isLotteryExtra(selectedLottery) ? (
                    <span className={`${styles.chip} ${styles.chipExtra}`}>Extraordinario</span>
                  ) : null}
                  {selectedClosed ? (
                    <span className={`${styles.chip} ${styles.chipClosed}`}>Cerrado</span>
                  ) : openBadge ? (
                    <span className={styles.chip}>{openBadge}</span>
                  ) : null}
                  <button
                    type="button"
                    className={styles.prizePlanBtn}
                    onClick={handlePrizePlan}
                  >
                    <FontAwesomeIcon icon={faFileLines} aria-hidden />
                    Plan de premios
                  </button>
                  {prize != null && prize !== "" ? (
                    <span className={styles.prizeChip}>
                      <small>Premio mayor</small>
                      {formatCurrency(prize)}
                    </span>
                  ) : null}
                </div>

                {selectedClosed ? (
                  <p className={styles.closedNotice} role="status">
                    {getClosingScheduleSummary(selectedLottery)}
                  </p>
                ) : null}

                {/* ¡Apuesta rápida! — Dame suerte */}
                <div className={styles.quickBet}>
                  <div className={styles.quickText}>
                    <p className={styles.quickTitle}>¡Apuesta rápida!</p>
                    <p className={styles.quickDesc}>
                      Obtén un número de la suerte y agrégalo al carrito.
                    </p>
                  </div>
                  <button
                    type="button"
                    className={styles.luckBtn}
                    onClick={generarAleatorios}
                    disabled={isAnimating || selectedClosed}
                    aria-busy={isAnimating}
                    title="¡Dame suerte!"
                  >
                    <span className={styles.slotIconWrap} aria-hidden="true">
                      <img
                        src={SLOT_MACHINE_ICON}
                        alt=""
                        className={`${styles.slotIcon} ${
                          isAnimating ? styles.slotIconSpinning : ""
                        }`}
                        onError={(e) => {
                          e.currentTarget.style.visibility = "hidden";
                        }}
                      />
                    </span>
                    <span>{isAnimating ? "Girando…" : "Dame suerte"}</span>
                  </button>
                </div>

                {/* Elige tu número */}
                <div className={styles.section}>
                  <div className={styles.sectionHead}>
                    <h3 className={styles.sectionTitle} id="bet-number-label">
                      Elige tu número
                    </h3>
                    <span className={styles.sectionHint}>4 cifras</span>
                  </div>
                  <div className={styles.reelsRow}>
                    <div
                      className={`${styles.slotMachineContainer} ${
                        isAnimating ? styles.slotMachineActive : ""
                      }`}
                      role="group"
                      aria-labelledby="bet-number-label"
                      aria-busy={isAnimating}
                    >
                      {[0, 1, 2, 3].map((i) => (
                        <div key={i} className={styles.slotWrapper}>
                          <div
                            className={`${styles.slotReel} ${
                              spinningSlots[i] ? styles.spinning : ""
                            } ${i === 3 && slowingDown ? styles.slowingDown : ""}`}
                            style={
                              i === 3 && slowingDown
                                ? { "--spin-speed": `${spinSpeed}s` }
                                : undefined
                            }
                          >
                            {reelsActive ? (
                              <span className={styles.slotNumber} aria-hidden="true">
                                {slotNumbers[i]}
                              </span>
                            ) : (
                              <input
                                ref={inputRefs[i]}
                                value={numero[i]}
                                maxLength={1}
                                className={styles.slotInput}
                                onChange={(e) => handleNumeroChange(i, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(i, e)}
                                onFocus={(e) => e.target.select()}
                                readOnly={isAnimating}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="off"
                                aria-label={`Dígito ${i + 1} de 4`}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className={styles.refreshBtn}
                      onClick={clearNumber}
                      disabled={isAnimating || numero.every((n) => n === "")}
                      aria-label="Borrar número"
                      title="Borrar número"
                    >
                      <FontAwesomeIcon icon={faRotateRight} />
                    </button>
                  </div>
                </div>

                {/* Fracciones — mismo flujo que Flutter FractionCounter */}
                <div className={styles.section}>
                  <div className={styles.fraccionesRow}>
                    <div className={styles.fraccionInfo}>
                      <h3 className={styles.sectionTitle}>Fracciones</h3>
                      <p className={styles.fraccionPrice}>
                        Fracción c/u <strong>{formatCurrency(precioFraccion)}</strong>
                      </p>
                    </div>
                    <div className={styles.stepper}>
                      <button
                        type="button"
                        className={styles.stepperBtn}
                        onClick={() => setCantidad((c) => Math.max(1, c - 1))}
                        disabled={selectedClosed || cantidad <= 1}
                        aria-label="Quitar fracción"
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                      <span className={styles.stepperValue} aria-live="polite">
                        <strong>{cantidad}</strong>
                        <small>UNDS</small>
                      </span>
                      <button
                        type="button"
                        className={styles.stepperBtn}
                        onClick={() => {
                          if (cantidad >= maxFracciones) {
                            setFormError("Es el máximo permitido en esta lotería.");
                            return;
                          }
                          setFormError("");
                          setCantidad((c) => Math.min(maxFracciones, c + 1));
                        }}
                        disabled={selectedClosed || cantidad >= maxFracciones}
                        aria-label="Agregar fracción"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>
                  </div>
                  <p
                    className={`${styles.fraccionHint} ${
                      cantidad >= maxFracciones ? styles.fraccionHintVisible : ""
                    }`}
                    aria-hidden={cantidad < maxFracciones}
                  >
                    Es el máximo permitido en esta lotería ({maxFracciones}).
                  </p>
                </div>

                {/* Elige la serie (solo series disponibles para el número) */}
                <div className={styles.section}>
                  <div className={styles.sectionHead}>
                    <h3 className={styles.sectionTitle}>Elige la serie</h3>
                    {availableSeries.length > 0 && !loadingSeries ? (
                      <span className={styles.sectionHint}>
                        {availableSeries.length} disponible{availableSeries.length === 1 ? "" : "s"}
                      </span>
                    ) : null}
                  </div>

                  {loadingSeries ? (
                    <div className={styles.seriesLoading} role="status">
                      <div className={styles.seriesSpinner}></div>
                      <span>Consultando series…</span>
                    </div>
                  ) : !numberComplete ? (
                    <p className={styles.seriesHint}>
                      Completa tu número para ver las series disponibles.
                    </p>
                  ) : availableSeries.length === 0 ? (
                    <p className={styles.seriesHint} role="status">
                      No hay series disponibles para este número.
                    </p>
                  ) : (
                    <div className={styles.seriesGrid}>
                      {availableSeries.map((s) => (
                        <button
                          type="button"
                          key={s}
                          className={`${styles.seriesBtn} ${series === s ? styles.seriesBtnActive : ""}`}
                          onClick={() => setSeries(s)}
                          disabled={selectedClosed}
                          aria-pressed={series === s}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <FeedbackMessage type="error" className={styles.feedbackFull}>
                  {builderError}
                </FeedbackMessage>
              </>
            ) : (
              <div className={styles.noLotteryMessage}>
                <img
                  src={SLOT_MACHINE_ICON}
                  alt=""
                  aria-hidden="true"
                  className={styles.noLotteryIcon}
                />
                <p className={styles.noLotteryTitle}>Elige una lotería</p>
                <p className={styles.noLotteryHintDesktop}>
                  Selecciona una de las tarjetas de la izquierda para armar tu
                  apuesta.
                </p>
                <p className={styles.noLotteryHintMobile}>
                  Selecciona una lotería arriba para comenzar a jugar.
                </p>
                <FeedbackMessage type="error" className={styles.feedbackFull}>
                  {builderError}
                </FeedbackMessage>
              </div>
            )}
          </section>
        </Reveal>

        {/* Paso 3 (derecha): resumen + total + añadir al carrito */}
        <Reveal variant="sharedAxisX" delay={0.16} className={styles.summaryColumn}>
          <BetSummarySidebar
            lottery={selectedLottery}
            numero={numero}
            series={series}
            cantidad={cantidad}
            maxFracciones={maxFracciones}
            total={total}
            now={now}
            canAdd={canAddToCart}
            hint={cartHint}
            error={cartError}
            onAddToCart={enviarDatos}
            onBack={handleBack}
          />
        </Reveal>
      </div>

      {/* Móvil: total + CTA siempre visibles abajo */}
      {selectedLottery ? (
        <div className={styles.mobileBar}>
          {cartError ? (
            <p className={styles.mobileBarError} aria-hidden="true">
              {cartError}
            </p>
          ) : null}
          <div className={styles.mobileBarInner}>
            <div className={styles.mobileBarTotal}>
              <span className={styles.mobileBarLabel}>
                Total · {cantidad} {cantidad === 1 ? "fracción" : "fracciones"}
              </span>
              <strong className={styles.mobileBarValue}>{formatCurrency(total)}</strong>
            </div>
            <button
              type="button"
              className={styles.mobileBarBtn}
              onClick={enviarDatos}
              disabled={!canAddToCart}
            >
              <FontAwesomeIcon icon={faCartPlus} aria-hidden />
              Añadir al carrito
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
